import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { StoreProvider, useStore } from "@/context/StoreContext";
import { api } from "@/lib/api";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CookieBanner from "@/components/CookieBanner";
import NewsletterPopup from "@/components/NewsletterPopup";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Account from "@/pages/Account";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import FAQs from "@/pages/FAQs";
import LegalPage from "@/pages/LegalPage";
import NotFound from "@/pages/NotFound";

import AdminLayout from "@/pages/admin/AdminLayout";
import { AdminLogin } from "@/pages/admin/AdminShared";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminDiscounts from "@/pages/admin/AdminDiscounts";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminContent from "@/pages/admin/AdminContent";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminDelivery from "@/pages/admin/AdminDelivery";

function Storefront({ children }) {
  return (
    <>
      <Header />
      <main className="min-h-[60vh]">{children}</main>
      <Footer />
      <CookieBanner />
      <NewsletterPopup />
    </>
  );
}

const S = (el) => <Storefront>{el}</Storefront>;

function AdminGuard({ children }) {
  const { user, authReady } = useStore();
  if (!authReady) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user || user.role !== "admin") return <AdminLogin />;
  return <AdminLayout>{children}</AdminLayout>;
}

function ScrollTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PageTracker() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    const t = setTimeout(() => {
      api.post("/track", { path: pathname, title: document.title, type: "page" }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <StoreProvider>
      <BrowserRouter>
        <ScrollTop />
        <PageTracker />
        <Routes>
          <Route path="/" element={S(<Home />)} />
          <Route path="/shop" element={S(<Shop />)} />
          <Route path="/product/:slug" element={S(<ProductDetail />)} />
          <Route path="/cart" element={S(<Cart />)} />
          <Route path="/checkout" element={S(<Checkout />)} />
          <Route path="/order-confirmation" element={S(<OrderConfirmation />)} />
          <Route path="/account" element={S(<Account />)} />
          <Route path="/about" element={S(<About />)} />
          <Route path="/contact" element={S(<Contact />)} />
          <Route path="/faqs" element={S(<FAQs />)} />
          <Route path="/delivery-returns" element={S(<LegalPage contentKey="delivery_returns" />)} />
          <Route path="/terms" element={S(<LegalPage contentKey="terms" />)} />
          <Route path="/privacy" element={S(<LegalPage contentKey="privacy" />)} />
          <Route path="/cookie-policy" element={S(<LegalPage contentKey="cookies" />)} />
          <Route path="/accessibility" element={S(<LegalPage contentKey="accessibility" />)} />

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
          <Route path="/admin/messages" element={<AdminGuard><AdminMessages /></AdminGuard>} />
          <Route path="/admin/analytics" element={<AdminGuard><AdminAnalytics /></AdminGuard>} />
          <Route path="/admin/discounts" element={<AdminGuard><AdminDiscounts /></AdminGuard>} />
          <Route path="/admin/reviews" element={<AdminGuard><AdminReviews /></AdminGuard>} />
          <Route path="/admin/customers" element={<AdminGuard><AdminCustomers /></AdminGuard>} />
          <Route path="/admin/content" element={<AdminGuard><AdminContent /></AdminGuard>} />
          <Route path="/admin/announcements" element={<AdminGuard><AdminAnnouncements /></AdminGuard>} />
          <Route path="/admin/delivery" element={<AdminGuard><AdminDelivery /></AdminGuard>} />

          <Route path="*" element={S(<NotFound />)} />
        </Routes>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </StoreProvider>
  );
}

export default App;
